import { Component, OnInit, ViewChild, inject } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
  ButtonConfig,
  InvoiceTableItem,
  buttonsStaticConfig,
  inputErrors,
  simpleTablePayout,
  RoutesEnum,
  PaymentFormConfig,
  getPaymentLabel,
  InvoicePaymentFunctions,
  InvoicePaymentsData,
  NotificationEnum,
  NotificationTimes,
  NotificationService, ErrorHandlingService
} from '../../../global';
import {Observable} from 'rxjs';
import {dialogInvoiceTable} from '../../../invoices/static/invoice-data-sheet-config';
import {UntypedFormArray} from '@angular/forms';
import {catchError, map, shareReplay} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {PayoutTemplateContainer} from '../../containers';
import {MODIFIED_ERROR_LABEL} from '../../../invoices/containers/invoices/invoice-data-form/invoice-data-form.config';
import { DialogContainerComponent } from '../../components/dialog-container/dialog-container.component';
import { SimpleTableComponent } from '../../components/simple-table/simple-table.component';
import { NgIf, AsyncPipe } from '@angular/common';
import { PayoutTemplateContainer as PayoutTemplateContainer_1 } from '../../containers/payout-template/payout-template.container';
import { LoaderComponent } from '../../components/loader/loader.component';

export interface InvoicePaymentDialogConfig {
  invoice: InvoiceTableItem;
  title: string;
  payments$: Observable<InvoicePaymentsData>;
  save: ((paymentData, versionLock) => Observable<any>);
}

@Component({
    selector: 'slm-invoice-payment-dialog',
    templateUrl: './invoice-payment-dialog.component.html',
    imports: [DialogContainerComponent, SimpleTableComponent, NgIf, PayoutTemplateContainer_1, LoaderComponent, AsyncPipe]
})
export class InvoicePaymentDialogComponent implements OnInit {
  private readonly dialogRef = inject<MatDialogRef<InvoicePaymentDialogComponent>>(MatDialogRef);
  private readonly translateService = inject(TranslateService);
  private readonly notification = inject(NotificationService);
  private readonly errorHandler = inject(ErrorHandlingService);
  data = inject<InvoicePaymentDialogConfig>(MAT_DIALOG_DATA);

  public readonly errors = inputErrors;
  public readonly tableConfig = [...dialogInvoiceTable];
  public readonly paymentTableConfig = [
    simpleTablePayout[0],
    simpleTablePayout[1],
    simpleTablePayout[2],
    simpleTablePayout[3]
  ];
  public readonly buttons: Array<ButtonConfig> = [
    buttonsStaticConfig.cancel,
    buttonsStaticConfig.saveData
  ];
  public formArray: UntypedFormArray;
  public payments$: Observable<Array<any>>;
  public paymentConfig$: Observable<PaymentFormConfig>;
  public versionLock: number;

  @ViewChild('payoutTemplate', {static: false})
  payoutTemplateContainer: PayoutTemplateContainer;

  ngOnInit() {
    const isOutgoing = this.data.title.includes('OUTGOINGS');
    const link = !isOutgoing ? RoutesEnum.INCOMING_INVOICE_DATA_SHEET : RoutesEnum.OUTGOINGS_INVOICE_DATA_SHEET;
    this.payments$ = this.data.payments$.pipe(
      map((response) => {
        this.versionLock = response?.versionLock;
        return response?.items || [];
      }),
      map(items => items.map(item => ({
        ...item,
        type: getPaymentLabel(item, item.type === 'cashDiscount' ?
          this.translateService.instant('INVOICE.PAYMENT_TYPE.DISCOUNT') + ' (' + item.percent + '%)'
          : link)
      }))),
      shareReplay({bufferSize: 1, refCount: true})
    );
    this.paymentConfig$ = this.payments$.pipe(
      map((items) => ({
        paidValue: items.reduce((amount, payment) => +payment.grossValue + amount, 0),
        partnerId: +this.data?.invoice?.partner?.id,
        date: this.data?.invoice?.paymentDeadline,
        documentId: +this.data?.invoice?.id,
        grossValue: +this.data?.invoice?.grossValue || 0,
        currency: this.data.invoice.currency,
        type: isOutgoing? 'outgoing': 'incoming',
        paymentType: this.data.invoice?.paymentType,
        savedInTransaction: this.data.invoice?.savedInTransaction
      }))
    );
    this.tableConfig.splice(2, 1);
  }

  public setPaymentForm(array: UntypedFormArray): void {
    this.formArray = array;
  }

  public buttonEvent(event: string) {
    if (event === 'cancel') {
      this.dialogRef.close();
      return;
    }
    if (!this.formArray) {
      return;
    }

    if (this.formArray.invalid && this.payoutTemplateContainer) {
      this.payoutTemplateContainer?.markAllAsTouched();
      return;
    }

    const paymentData = InvoicePaymentFunctions.getPaymentData(this.formArray);
    if(!paymentData){
      return;
    }

    this.dialogRef.disableClose = true;
    this.buttons[1].isLoading = true;
    this.data.save(paymentData, this.versionLock).pipe(
      catchError((error) => {
        const errorMsg = error?.error || null;

        if(errorMsg?.error && errorMsg.error === MODIFIED_ERROR_LABEL){
          this.notification.notify('INVOICE.INVOICE_PREV_EDITED', NotificationEnum.ERROR, NotificationTimes.INFINITE);
        }

        return this.errorHandler.handleError(error);
      })
    ).subscribe({
      error: () => {
        this.dialogRef.disableClose = false;
        this.buttons[1].isLoading = false;
      },
      next: () => {
        this.dialogRef.disableClose = false;
        this.buttons[1].isLoading = false;
        this.dialogRef.close(true);
      }
    });
  }
}
